/**
 * Mix design arithmetic.
 *
 * There is exactly one computed value on the sheet — the mass total — and it is
 * display-only. Nothing here judges a mix: a mix design is an authored
 * document, and inventing an acceptance rule for one would be fabricating a
 * verdict the author never gave.
 */

import type { MixDesignMaterialInput } from '../types';
import { roundTo } from './numbers';

/**
 * A materials-row amount as a number, or null when it is not one.
 *
 * Null is the normal case for the Air Content row, whose amount is a RANGE
 * ('1-4'). A range has no single value and must never be summed as if it did.
 */
export function parseAmount(amount: string | null | undefined): number | null {
  if (typeof amount !== 'string') return null;
  const trimmed = amount.trim();
  if (trimmed === '') return null;
  // Number('') is 0 and Number('1-4') is NaN; both are handled, but a leading
  // '+' or whitespace-only string must not slip through as 0.
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Total mass in kg, matching how the printed sheet totals its column.
 *
 * Kilograms count; litres count kilogram-for-litre (water at 1 kg/L, which is
 * how the CC520N sheet sums its own rows to 2430); millilitre admixtures and
 * percentages do not count. Rounded to 3 dp to keep binary floating-point noise off a document
 * people read.
 */
export function mixMassTotalKg(materials: readonly MixDesignMaterialInput[]): number {
  let total = 0;
  for (const material of materials) {
    if (material.unit !== 'kg' && material.unit !== 'L') continue;
    const amount = parseAmount(material.amount);
    if (amount === null) continue;
    total += amount;
  }
  return roundTo(total, 3) ?? 0;
}
